<template>
  <span class="lead-quality-btn-wrap">
    <button
      v-tooltip="tooltip"
      type="button"
      class="lead-quality-icon-btn"
      :disabled="loading"
      :title="tooltip"
      :aria-label="tooltip"
      @click.stop="onGenerate"
    >
      <span v-if="loading" class="lead-quality-spinner" aria-hidden="true" />
      <svg
        v-else
        class="lead-quality-icon"
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path
          d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z"
        />
        <path d="M5 19h14" />
      </svg>
    </button>

    <woot-modal
      :show="showResultModal"
      :on-close="closeResultModal"
      content-class="lead-quality-result-modal"
    >
      <woot-modal-header :header-title="$t('LEAD_QUALITY.MODAL_TITLE')" />
      <div v-if="!isEditing" class="lead-quality-result">
        <div
          class="lead-quality-result__block lead-quality-result__block--score"
        >
          <p class="lead-quality-result__label">
            {{ $t('LEAD_QUALITY.SCORE_LABEL') }}
          </p>
          <p class="lead-quality-result__score">
            {{ modalScoreDisplay }}
          </p>
        </div>
        <div
          class="lead-quality-result__block lead-quality-result__block--reasons"
        >
          <p class="lead-quality-result__label">
            {{ $t('LEAD_QUALITY.REASONS_LABEL') }}
          </p>
          <p class="lead-quality-result__reasons">{{ displayReasonsReadonly }}</p>
        </div>
        <div
          class="lead-quality-result__actions lead-quality-result__actions--row"
        >
          <woot-button
            color-scheme="alert"
            size="small"
            :disabled="actionLoading"
            @click="openRemoveConfirm"
          >
            {{ $t('LEAD_QUALITY.REMOVE_CROWN') }}
          </woot-button>
          <woot-button
            color-scheme="secondary"
            size="small"
            :disabled="actionLoading"
            @click="startEdit"
          >
            {{ $t('LEAD_QUALITY.EDIT') }}
          </woot-button>
          <woot-button
            color-scheme="primary"
            size="small"
            :disabled="actionLoading"
            @click="closeResultModal"
          >
            {{ $t('LEAD_QUALITY.MODAL_OK') }}
          </woot-button>
        </div>
      </div>
      <div v-else class="lead-quality-result lead-quality-result--edit">
        <div class="lead-quality-edit-field lead-quality-edit-field--score">
          <woot-input
            v-model="editScore"
            type="number"
            :label="$t('LEAD_QUALITY.SCORE_LABEL')"
            :placeholder="$t('LEAD_QUALITY.SCORE_PLACEHOLDER')"
            :error="editScoreError"
            :styles="{ minHeight: '2.5rem' }"
          />
        </div>
        <div class="lead-quality-edit-field">
          <label class="lead-quality-edit-field__label">{{
            $t('LEAD_QUALITY.REASONS_LABEL')
          }}</label>
          <textarea
            v-model="editReasons"
            class="lead-quality-edit-textarea"
            rows="4"
            :maxlength="reasonsMaxLength"
            :placeholder="$t('LEAD_QUALITY.REASONS_PLACEHOLDER')"
          />
          <p class="lead-quality-edit-reasons-meta">
            <span class="lead-quality-edit-reasons-count">{{
              editReasonsCharCount
            }}</span>
          </p>
          <p v-if="editReasonsError" class="lead-quality-edit-field-error">
            {{ editReasonsError }}
          </p>
        </div>
        <div
          class="lead-quality-result__actions lead-quality-result__actions--row"
        >
          <woot-button
            color-scheme="primary"
            size="small"
            :is-loading="actionLoading"
            :is-disabled="
              actionLoading || !!editScoreError || !!editReasonsError
            "
            @click="saveEdit"
          >
            {{ $t('LEAD_QUALITY.EDIT_SAVE') }}
          </woot-button>
          <woot-button
            variant="clear"
            size="small"
            :disabled="actionLoading"
            @click="cancelEdit"
          >
            {{ $t('LEAD_QUALITY.EDIT_CANCEL') }}
          </woot-button>
        </div>
      </div>
    </woot-modal>

    <woot-delete-modal
      v-if="showRemoveConfirm"
      :show="true"
      :on-close="onRemoveDialogClose"
      :on-confirm="confirmRemoveCrown"
      :title="$t('LEAD_QUALITY.REMOVE_CONFIRM_TITLE')"
      :message="$t('LEAD_QUALITY.REMOVE_CONFIRM_MESSAGE')"
      :confirm-text="$t('LEAD_QUALITY.REMOVE_CONFIRM_YES')"
      :reject-text="$t('LEAD_QUALITY.REMOVE_CONFIRM_NO')"
    />
  </span>
</template>

<script>
import { mapActions } from 'vuex';
import {
  generateLeadQuality,
  updateLeadQualityManually,
  clearLeadQuality,
} from 'dashboard/api/leadQuality';
/** Keep in sync with `ContactLeadQualitiesController::LEAD_QUALITY_REASONS_MAX_LENGTH`. */
const LEAD_QUALITY_REASONS_MAX_LEN = 800;

export default {
  name: 'LeadQualityButton',
  props: {
    accountId: { type: [Number, String], required: true },
    contactId: { type: [Number, String], required: true },
  },
  data() {
    return {
      reasonsMaxLength: LEAD_QUALITY_REASONS_MAX_LEN,
      loading: false,
      showResultModal: false,
      modalScore: null,
      modalReasons: '',
      isEditing: false,
      showRemoveConfirm: false,
      actionLoading: false,
      editScore: '',
      editReasons: '',
    };
  },
  computed: {
    tooltip() {
      return this.$t('LEAD_QUALITY.GENERATE') || 'Score lead quality';
    },
    modalScoreDisplay() {
      if (this.modalScore == null || this.modalScore === '') return '—';
      return `${this.modalScore} / 100`;
    },
    displayReasonsReadonly() {
      const t = (this.modalReasons || '').trim();
      if (t) return t;
      return this.$t('LEAD_QUALITY.NO_REASONS') || '';
    },
    editScoreError() {
      if (!this.isEditing) return '';
      if (this.editScore === '' || this.editScore === null) {
        return this.$t('LEAD_QUALITY.EDIT_ERROR_SCORE');
      }
      const n = Number(this.editScore);
      if (!Number.isFinite(n) || n < 1 || n > 100) {
        return this.$t('LEAD_QUALITY.EDIT_ERROR_SCORE');
      }
      return '';
    },
    editReasonsError() {
      if (!this.isEditing) return '';
      const len = (this.editReasons || '').length;
      if (len > this.reasonsMaxLength) {
        return this.$t('LEAD_QUALITY.EDIT_ERROR_REASONS_LENGTH', {
          max: this.reasonsMaxLength,
        });
      }
      return '';
    },
    editReasonsCharCount() {
      return this.$t('LEAD_QUALITY.REASONS_CHAR_COUNT', {
        current: (this.editReasons || '').length,
        max: this.reasonsMaxLength,
      });
    },
  },
  methods: {
    ...mapActions('contacts', ['updateContact']),
    closeResultModal() {
      this.showResultModal = false;
      this.isEditing = false;
    },
    /** Open the result modal for existing metadata (e.g. crown click). */
    openResultFromMetadata(meta) {
      if (!meta || typeof meta !== 'object' || Object.keys(meta).length === 0) {
        return;
      }
      const merged = { ...meta };
      this.applyMetadataToModal(merged);
      this.isEditing = false;
      this.showRemoveConfirm = false;
      this.showResultModal = true;
      this.$emit('quality', {
        contactId: Number(this.contactId),
        lead_quality_metadata: merged,
      });
      this.updateContact({
        id: Number(this.contactId),
        lead_quality_metadata: merged,
      });
    },
    startEdit() {
      this.isEditing = true;
      this.editScore =
        this.modalScore != null && this.modalScore !== ''
          ? String(this.modalScore)
          : '';
      this.editReasons = (this.modalReasons || '').slice(
        0,
        this.reasonsMaxLength
      );
    },
    cancelEdit() {
      this.isEditing = false;
    },
    openRemoveConfirm() {
      this.showRemoveConfirm = true;
    },
    onRemoveDialogClose() {
      this.showRemoveConfirm = false;
    },
    async confirmRemoveCrown() {
      this.actionLoading = true;
      this.showRemoveConfirm = false;
      try {
        const data = await clearLeadQuality(this.accountId, this.contactId);
        const meta = data.lead_quality_metadata || {};
        this.updateContact({
          id: Number(this.contactId),
          lead_quality_metadata: meta,
        });
        this.$emit('quality', {
          contactId: Number(this.contactId),
          lead_quality_metadata: meta,
        });
        this.closeResultModal();
      } catch (e) {
        this.$emit('error', this.$t('LEAD_QUALITY.REMOVE_ERROR'));
      } finally {
        this.actionLoading = false;
      }
    },
    async saveEdit() {
      if (this.editScoreError || this.editReasonsError) return;
      const score = Math.round(Number(this.editScore));
      this.actionLoading = true;
      try {
        const data = await updateLeadQualityManually(
          this.accountId,
          this.contactId,
          {
            lead_quality_score: score,
            reasons: (this.editReasons || '').trim(),
          }
        );
        const meta = data.lead_quality_metadata;
        if (meta) {
          this.applyMetadataToModal(meta);
          this.updateContact({
            id: Number(this.contactId),
            lead_quality_metadata: meta,
          });
          this.$emit('quality', {
            contactId: Number(this.contactId),
            lead_quality_metadata: meta,
          });
        }
        this.isEditing = false;
      } catch (e) {
        this.$emit('error', this.$t('LEAD_QUALITY.UPDATE_ERROR'));
      } finally {
        this.actionLoading = false;
      }
    },
    applyMetadataToModal(meta) {
      if (!meta || typeof meta !== 'object') {
        this.modalScore = null;
        this.modalReasons = '';
        return;
      }
      const rawScore = meta.lead_quality_score;
      this.modalScore =
        rawScore != null && rawScore !== '' && Number.isFinite(Number(rawScore))
          ? Number(rawScore)
          : null;
      this.modalReasons = (meta.reasons ?? '').toString();
    },
    async onGenerate() {
      this.loading = true;
      try {
        const data = await generateLeadQuality(this.accountId, this.contactId);
        const meta =
          data.lead_quality_metadata ||
          (data.lead_quality_score != null
            ? {
                lead_quality_score: data.lead_quality_score,
                reasons: data.reasons || '',
                generated_at: new Date().toISOString(),
              }
            : null);
        if (meta) {
          this.applyMetadataToModal(meta);
          this.updateContact({
            id: Number(this.contactId),
            lead_quality_metadata: meta,
          });
          this.isEditing = false;
          this.showResultModal = true;
        }
        this.$emit('quality', {
          contactId: Number(this.contactId),
          lead_quality_metadata: meta,
        });
      } catch (e) {
        this.$emit(
          'error',
          this.$t('LEAD_QUALITY.ERROR') || 'Something went wrong.'
        );
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.lead-quality-btn-wrap {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-left: 0.125rem;
}

/*
 * Contact "More details" modal uses a raised stacking context (e.g. z-index 40 + transform).
 * Label picker and similar UIs use --z-index-very-high (9999). Default .modal-mask is z-[34],
 * so the lead quality overlay was painted under those panels. Scope the lift to this wrapper only.
 */
.lead-quality-btn-wrap :deep(.modal-mask) {
  z-index: calc(var(--z-index-very-high, 9999) + 2);
}

.lead-quality-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem;
  border: none;
  background: transparent;
  color: #b45309;
  cursor: pointer;
  border-radius: 4px;
}
.lead-quality-icon-btn:hover:not(:disabled) {
  background: var(--color-background-light, rgba(0, 0, 0, 0.06));
}
.lead-quality-icon-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.lead-quality-icon {
  display: block;
  flex-shrink: 0;
}
.lead-quality-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: lead-quality-spin 0.6s linear infinite;
}
@keyframes lead-quality-spin {
  to {
    transform: rotate(360deg);
  }
}

.lead-quality-edit-field {
  margin-bottom: 0.75rem;
}

.lead-quality-edit-field__label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgb(100 116 139);
}

html.dark .lead-quality-edit-field__label {
  color: rgb(148 163 184);
}

.lead-quality-edit-textarea {
  width: 100%;
  box-sizing: border-box;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  border-radius: 0.375rem;
  border: 1px solid rgb(226 232 240);
  background: rgb(255 255 255);
  color: rgb(15 23 42);
  resize: vertical;
  min-height: 5rem;
  max-height: 14rem;
  overflow-y: auto;
}

html.dark .lead-quality-edit-textarea {
  border-color: rgb(51 65 85);
  background: rgb(15 23 42);
  color: rgb(241 245 249);
}

.lead-quality-edit-field--score :deep(.input-container .message) {
  color: rgb(220 38 38);
  font-size: 0.75rem;
  font-weight: 500;
}

html.dark .lead-quality-edit-field--score :deep(.input-container .message) {
  color: rgb(248 113 113);
}

.lead-quality-edit-reasons-meta {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  color: rgb(100 116 139);
}

html.dark .lead-quality-edit-reasons-meta {
  color: rgb(148 163 184);
}

.lead-quality-edit-field-error {
  margin: 0.25rem 0 0;
  font-size: 0.75rem;
  font-weight: 500;
  color: rgb(220 38 38);
}

html.dark .lead-quality-edit-field-error {
  color: rgb(248 113 113);
}
</style>

<!-- Narrower modal, rounded corners, tightened header/body -->
<style lang="scss">
.modal-container.lead-quality-result-modal {
  width: min(28rem, calc(100vw - 1.5rem));
  max-width: 28rem;
  max-height: min(90vh, 40rem);
  display: flex;
  flex-direction: column;
  border-radius: 0.75rem;
  overflow: hidden;
  box-shadow: 0 10px 15px -3px rgba(15, 23, 42, 0.08),
    0 4px 6px -4px rgba(15, 23, 42, 0.06);
  border: 1px solid rgb(226 232 240);
}

html.dark .modal-container.lead-quality-result-modal {
  border-color: rgb(51 65 85);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.35),
    0 4px 6px -4px rgba(0, 0, 0, 0.25);
}

.modal-container.lead-quality-result-modal button.modal--close + div {
  flex-shrink: 0;
  padding: 0.75rem 1.25rem 0 !important;
  border-bottom: 1px solid rgb(241 245 249);
}

html.dark .modal-container.lead-quality-result-modal button.modal--close + div {
  border-bottom-color: rgb(51 65 85);
}

.modal-container.lead-quality-result-modal button.modal--close + div h2 {
  font-size: 1rem;
  line-height: 1.35;
  font-weight: 600;
  letter-spacing: -0.01em;
}

.lead-quality-result {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding: 1rem 1.5rem 1.125rem;
}

.lead-quality-result--edit {
  padding-top: 0.75rem;
}

.lead-quality-result__label {
  margin: 0 0 0.25rem;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: rgb(100 116 139);
}

html.dark .lead-quality-result__label {
  color: rgb(148 163 184);
}

.lead-quality-result__block--score {
  margin-bottom: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: rgb(248 250 252);
  border: 1px solid rgb(226 232 240);
}

html.dark .lead-quality-result__block--score {
  background: rgb(30 41 59);
  border-color: rgb(51 65 85);
}

.lead-quality-result__score {
  margin: 0;
  font-size: 1.625rem;
  line-height: 1.25;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: #b45309;
}

html.dark
  .modal-container.lead-quality-result-modal
  .lead-quality-result__score {
  color: #fbbf24;
}

.lead-quality-result__block--reasons {
  margin-bottom: 0;
}

.lead-quality-result__reasons {
  margin: 0;
  font-size: 0.875rem;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-word;
  color: rgb(51 65 85);
}

html.dark .lead-quality-result__reasons {
  color: rgb(226 232 240);
}

.lead-quality-result__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
  padding-top: 0.75rem;
  border-top: 1px solid rgb(241 245 249);
}

.lead-quality-result__actions--row {
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

html.dark .lead-quality-result__actions {
  border-top-color: rgb(51 65 85);
}

/* Compact dismiss (X): default clear button + top-2 is tall; shrink hit area so hover does not cross header rule */
.modal-container.lead-quality-result-modal .modal--close.button {
  top: 0.35rem;
  right: 0.35rem;
  padding: 0.125rem !important;
  min-width: 0 !important;
  min-height: 0 !important;
  width: 1.5rem;
  height: 1.5rem;
  line-height: 1;
  border-radius: 0.25rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.modal-container.lead-quality-result-modal .modal--close.button .icon {
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 0;
}

.modal-container.lead-quality-result-modal .modal--close.button .icon svg {
  width: 13px !important;
  height: 13px !important;
  max-width: 13px;
  max-height: 13px;
}
</style>
